import {
  AlertCircle,
  AlertTriangle,
  Info,
  CheckCircle,
  Shield,
  Eye,
  Target,
  Zap,
  TrendingUp,
  Lightbulb,
} from 'lucide-react';
import { createElement } from 'react';
import type {
  Finding,
  NormalizedSeverity,
  CategoryItem,
  EffortEstimate,
} from '@/components/audit/ProReport/types';

export function normalizeSeverity(severity: string): NormalizedSeverity {
  const severityMap: Record<string, NormalizedSeverity> = {
    critical: 'critical',
    serious: 'high',
    high: 'high',
    moderate: 'medium',
    medium: 'medium',
    low: 'low',
    minor: 'low',
  };
  return severityMap[severity.toLowerCase()] || 'medium';
}

export function getSeverityIcon(severity: string, size = 18) {
  const normalized = normalizeSeverity(severity);
  switch (normalized) {
    case 'critical':
      return createElement(AlertCircle, { size });
    case 'high':
      return createElement(AlertTriangle, { size });
    case 'medium':
      return createElement(Info, { size });
    default:
      return createElement(CheckCircle, { size });
  }
}

export function getSeverityLabel(severity: string): string {
  const normalized = normalizeSeverity(severity);
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

export function getImpactDescription(severity: string): string {
  const normalized = normalizeSeverity(severity);
  const impacts: Record<string, string> = {
    // Severity-neutral wording. The previous strings carried percentages
    // ("Blocks 30%+ of users") that no data in this repository supports.
    critical: 'Blocks the task for the visitors it affects',
    high: 'Makes the task noticeably harder',
    medium: 'Adds friction at this step',
    low: 'Minor effect on the experience',
  };
  return impacts[normalized] || impacts.medium;
}

/**
 * Effort for a finding card.
 *
 * Prefers the model's own per-finding estimate, which the evidence prompt asks
 * for and the report persists. Falls back to a category default only for reports
 * generated before that field existed. FindingCard used to read the category
 * default unconditionally while CategorySection on the same page read the
 * model's field, so one finding could show two different efforts.
 */
export function getFindingEffort(finding: Finding): EffortEstimate {
  switch (finding.effortEstimate) {
    case 'quick':
      return { label: 'Quick Fix', bars: 1 };
    case 'medium':
      return { label: 'Medium', bars: 2 };
    case 'complex':
      return { label: 'Complex', bars: 3 };
    default:
      return getEffortEstimate(deriveCategory(finding));
  }
}

export function getEffortEstimate(category: string): EffortEstimate {
  const effortMap: Record<string, EffortEstimate> = {
    'WCAG Accessibility': { label: 'Quick Fix', bars: 1 },
    // deriveCategory names this bucket 'Accessibility'; without this row a
    // critical missing alt text defaulted to Medium and could never rank first.
    Accessibility: { label: 'Quick Fix', bars: 1 },
    'Visual Hierarchy': { label: 'Medium', bars: 2 },
    'Navigation': { label: 'Complex', bars: 3 },
    'Forms & Input': { label: 'Medium', bars: 2 },
    'Mobile Usability': { label: 'Medium', bars: 2 },
    'Performance': { label: 'Complex', bars: 3 },
    'Content & Copy': { label: 'Quick Fix', bars: 1 },
    'Trust & Credibility': { label: 'Medium', bars: 2 },
  };
  return effortMap[category] || { label: 'Medium', bars: 2 };
}

/**
 * Keyword rules on word boundaries. The previous substring rules misfiled
 * findings whose text merely contained a keyword: "per(form)ance" and
 * "in(form)ation" landed in Forms & Input, "con(text)" in Content & Copy,
 * "(link)ed" in Navigation. Each rule is a whole-word (or whole-phrase) match;
 * plural forms are listed explicitly. The model's own effort estimate is read
 * first by getFindingEffort, so this only decides the category default for
 * reports that predate the field.
 */
const CATEGORY_RULES: Array<[string, RegExp]> = [
  ['Accessibility', /\b(accessibility|wcag|aria|screen readers?|alt text|contrast|keyboard|focus|skip links?)\b/],
  ['Navigation', /\b(navigation|menus?|breadcrumbs?|links?|back button|sitemap)\b/],
  ['Forms & Input', /\b(forms?|inputs?|fields?|labels?|validation|submit|placeholders?)\b/],
  ['Visual Hierarchy', /\b(hierarchy|headings?|typography|fonts?|spacing|whitespace|visual|layout)\b/],
  ['Mobile Usability', /\b(mobile|touch|tap targets?|responsive|viewport|swipe)\b/],
  ['Performance', /\b(performance|loading|speed|lazy|optimi[sz]e[ds]?)\b/],
  ['Content & Copy', /\b(content|copy|text|readability|microcopy|language)\b/],
  ['Trust & Credibility', /\b(trust|security|privacy|credentials?|credibility)\b/],
];

export function deriveCategory(finding: Finding): string {
  if (finding.category) return finding.category;

  const text = `${finding.principleTitle || ''} ${finding.title || ''} ${finding.description || ''}`.toLowerCase();
  for (const [category, rule] of CATEGORY_RULES) {
    if (rule.test(text)) return category;
  }
  return 'UX Issue';
}

export function extractCategories(findings: Finding[]): CategoryItem[] {
  const counts = findings.reduce(
    (acc, f) => {
      const category = deriveCategory(f);
      acc[category] = (acc[category] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  const iconMap: Record<string, React.ReactNode> = {
    'WCAG Accessibility': createElement(Shield, { size: 16 }),
    Accessibility: createElement(Shield, { size: 16 }),
    'Visual Hierarchy': createElement(Eye, { size: 16 }),
    Navigation: createElement(Target, { size: 16 }),
    'Forms & Input': createElement(Zap, { size: 16 }),
    'Mobile Usability': createElement(TrendingUp, { size: 16 }),
  };

  return Object.entries(counts)
    .map(([name, count]) => ({
      name,
      count,
      icon: iconMap[name] || createElement(Lightbulb, { size: 16 }),
    }))
    .sort((a, b) => b.count - a.count);
}

export function extractUniquePrinciples(findings: Finding[]): string[] {
  const principles = new Set<string>();
  findings.forEach((f) => {
    if (f.principleTitle) principles.add(f.principleTitle);
    if (f.principle) principles.add(f.principle);
  });
  return Array.from(principles).slice(0, 12);
}

export function formatDate(dateString: string, locale = 'en'): string {
  return new Date(dateString).toLocaleDateString(locale === 'es' ? 'es-ES' : 'en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function formatTime(dateString: string, locale = 'en'): string {
  return new Date(dateString).toLocaleTimeString(locale === 'es' ? 'es-ES' : 'en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function getNumberFormatter(locale = 'en'): Intl.NumberFormat {
  return new Intl.NumberFormat(locale === 'es' ? 'es-ES' : 'en-US');
}
